import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import type { TimingPeak } from '@shared/doseTimingPattern';
import {
  doseDotRadius,
  rowOpacityForOffset,
  type TimingDayRow,
} from '../../lib/doseTimingGrouping';

// A stacked-rows 24h timeline. One row per day, today at the top.
// Each dose plots as a dot at its local-TZ minute-of-day; dot radius
// scales with mg (square-root) so big doses read bigger but tiny ones
// stay visible. Older rows fade by recency so today's pattern is the
// dominant read.
//
// Habitual-time peaks (from findHabitualDoseTimes) render as soft
// vertical bands spanning all rows — a subtle "you usually dose here"
// cue without being prescriptive.

interface DoseTimingChartProps {
  rows: TimingDayRow[];
  habitualPeaks?: TimingPeak[];
}

const ROW_HEIGHT = 28;
const TOP_MARGIN = 8;
const BOTTOM_MARGIN = 26;
// Row labels read "Today" / "Yesterday" / "N days ago" — the widest
// realistic label ("13 days ago" at fontSize 11) is ≈ 55 px. We scale
// the gutter with viewport so we don't burn 22 % of a 360 px-wide phone
// on whitespace, while still letting roomy desktops keep the original
// breathing room.
const MIN_LABEL_WIDTH = 56;
const MAX_LABEL_WIDTH = 84;
const RIGHT_MARGIN = 16;
const HOUR_BAND_MINUTES = 30; // width of each habitual-time vertical band

export function DoseTimingChart({ rows, habitualPeaks }: DoseTimingChartProps) {
  const totalHeight = TOP_MARGIN + rows.length * ROW_HEIGHT + BOTTOM_MARGIN;
  return (
    <div style={{ width: '100%', height: totalHeight }}>
      <ParentSize>
        {({ width }) => (
          <DoseTimingChartInner
            rows={rows}
            habitualPeaks={habitualPeaks}
            width={width}
            height={totalHeight}
          />
        )}
      </ParentSize>
    </div>
  );
}

function clockLabel(hour: number): string {
  if (hour === 0 || hour === 24) return '12a';
  if (hour === 12) return 'noon';
  if (hour < 12) return `${hour}a`;
  return `${hour - 12}p`;
}

function DoseTimingChartInner({
  rows,
  habitualPeaks,
  width,
  height,
}: DoseTimingChartProps & { width: number; height: number }) {
  // 18 % of viewport clamped to [56, 84] — gives 65 px on a 360 px
  // phone, 75 px on a 414 px phone, and the original 84 px once we
  // cross ~470 px (tablet / sidebar layouts).
  const labelWidth = Math.round(
    Math.max(MIN_LABEL_WIDTH, Math.min(MAX_LABEL_WIDTH, width * 0.18)),
  );
  const innerW = Math.max(0, width - labelWidth - RIGHT_MARGIN);
  if (width === 0 || innerW === 0 || rows.length === 0) return null;

  const x = scaleLinear<number>({ domain: [0, 1440], range: [0, innerW] });
  const rowsBottom = TOP_MARGIN + rows.length * ROW_HEIGHT;
  // Bottom-axis hour ticks. Default every 6 h ("12a, 6a, noon, 6p, 12a")
  // fits down to ~200 px inner width. Below that, drop to every 12 h so
  // the labels don't overlap on narrow embeds.
  const hourTickValues = innerW < 200 ? [0, 12, 24] : [0, 6, 12, 18, 24];

  return (
    <svg width={width} height={height} role="img" aria-label="dose timing chart">
      {/* Habitual-time bands. Span the full row stack so the eye reads
          them as "usually dose around here" rather than per-day. */}
      {(habitualPeaks ?? []).map((p) => {
        const [hh, mm] = p.time.split(':').map((s) => Number.parseInt(s, 10));
        const minute = (Number.isFinite(hh) ? hh : 0) * 60 + (Number.isFinite(mm) ? mm : 0);
        const bandX = labelWidth + x(minute);
        const bandW = Math.max(4, x(HOUR_BAND_MINUTES) - x(0));
        return (
          <rect
            key={`band-${p.time}`}
            x={bandX - bandW / 2}
            y={TOP_MARGIN}
            width={bandW}
            height={rows.length * ROW_HEIGHT}
            fill="var(--chart-grid)"
            fillOpacity={0.35}
            rx={2}
          />
        );
      })}

      {/* Per-row horizontal guide + label + dose dots. */}
      {rows.map((row, r) => {
        const cy = TOP_MARGIN + r * ROW_HEIGHT + ROW_HEIGHT / 2;
        const opacity = rowOpacityForOffset(row.dayOffset);
        return (
          <g key={row.date}>
            <line
              x1={labelWidth}
              y1={cy}
              x2={labelWidth + innerW}
              y2={cy}
              stroke="var(--chart-grid)"
              strokeOpacity={0.45}
            />
            <text
              x={labelWidth - 8}
              y={cy}
              textAnchor="end"
              dominantBaseline="middle"
              fill="var(--chart-axis)"
              fontSize={11}
              opacity={opacity}
            >
              {row.label}
            </text>
            {row.doses.map((dose, i) => (
              <circle
                key={`${row.date}-${i}`}
                cx={labelWidth + x(dose.minuteOfDay)}
                cy={cy}
                r={doseDotRadius(dose.mg)}
                fill="var(--chart-actual)"
                fillOpacity={opacity}
              >
                <title>{`${row.label} — ${formatTime(dose.minuteOfDay)} · ${dose.mg.toFixed(1)} mg`}</title>
              </circle>
            ))}
          </g>
        );
      })}

      {/* Bottom axis: hour ticks every 6 hours. */}
      <line
        x1={labelWidth}
        y1={rowsBottom + 2}
        x2={labelWidth + innerW}
        y2={rowsBottom + 2}
        stroke="var(--chart-axis)"
        strokeOpacity={0.6}
      />
      {hourTickValues.map((h) => (
        <g key={`tick-${h}`}>
          <line
            x1={labelWidth + x(h * 60)}
            y1={rowsBottom + 2}
            x2={labelWidth + x(h * 60)}
            y2={rowsBottom + 6}
            stroke="var(--chart-axis)"
            strokeOpacity={0.6}
          />
          <text
            x={labelWidth + x(h * 60)}
            y={rowsBottom + 18}
            textAnchor="middle"
            fill="var(--chart-axis)"
            fontSize={11}
          >
            {clockLabel(h)}
          </text>
        </g>
      ))}
    </svg>
  );
}

function formatTime(minuteOfDay: number): string {
  const h = Math.floor(minuteOfDay / 60);
  const m = minuteOfDay % 60;
  const period = h < 12 ? 'a' : 'p';
  const hh = h === 0 ? 12 : h <= 12 ? h : h - 12;
  return `${hh}:${String(m).padStart(2, '0')}${period}`;
}
